import { Box, Grid, Paper } from '@mui/material';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { selectProject } from '../../features/project/projectSlice';
import { IDeal, IDealDeposit, IDealRealtor, IMortgage } from '../../types/CreateDealForm';
import { convertAllDates, numToCurrency, convertDate, getSalesStatusCodes, netHST } from '../../utils/Functions';
import { ICoopDeal } from './SearchPage';
import { IDocuments } from '../../types/docusign';

const DealCard = (props: ChildProps) => {
  const project = useSelector(selectProject);
  const { deal, coopRates, location, cancelled } = props;

  const depositCheck = (deposits: IDealDeposit[]) => {
    const allDeposits = deposits.every(
      (deposit) =>
        deposit.chequeType === 'received' ||
        deposit.chequeType === 'distributed' ||
        deposit.chequeType === 'cleared' ||
        deposit.chequeType === 'wire' ||
        deposit.chequeType === 'floating' ||
        deposit.chequeType === 'pad' ||
        deposit.name === 'Occupancy Deposit' ||
        deposit.name === 'Occupancy'
    );
    if (allDeposits) {
      return 'Deposits Received';
    } else {
      return 'Deposits Missing';
    }
  };

  const commissionCheck = (deal: string, documents: IDocuments[], realtor: any) => {
    let coopRate: any = coopRates.find((coopRate: ICoopDeal) => coopRate.deal === deal);
    if (coopRate) {
      let rates = coopRate.coopRates.reduce((a: any, b: any) => {
        return a + b.percentage;
      }, 0);
      if (rates) {
        let coopDocuments = documents.filter((document: IDocuments) => document.type === 'Coop');
        if (coopDocuments.length === 0) {
          return `${rates}% (None Created)`;
        }
        if (coopDocuments.length === 1) {
          return `${rates}% (${coopDocuments[0].status})`;
        }
        if (coopDocuments.length > 1) {
          let selected = coopDocuments.find((document: IDocuments) => document.name.includes(realtor[0].fullName));
          if (selected) {
            return `${rates}% (${selected.status})`;
          }
        }
      } else return 'None';
    } else return 'N/A';
  };

  const statusDate = (deal: IDeal) => {
    if (deal.unit.status === 'C') {
      return <strong>({convertAllDates(new Date(deal.executeDate!), 'PP')})</strong>;
    }
    if (getSalesStatusCodes(project).includes(deal.unit.status)) {
      return <strong>({convertAllDates(new Date(deal.firmDate!), 'PP')})</strong>;
    }
    if (deal.unit.status === 'S') {
      return <strong>({convertAllDates(new Date(deal.signDate!), 'PP')})</strong>;
    }
    if (deal.unit.status === 'O') {
      return <strong>({convertAllDates(new Date(deal.createdAt), 'PP')})</strong>;
    }
    return '';
  };

  const statusColour = (deal: IDeal) => {
    if (deal.unit.status === 'C') {
      return '#d3edb7';
    }
    if (getSalesStatusCodes(project).includes(deal.unit.status)) {
      return '#8ad6f1';
    }
    if (deal.unit.status === 'S') {
      return '#d5a2ff';
    }
    if (deal.unit.status === 'O') {
      return '#e6b8b7';
    }
    return 'white';
  };

  const mortgageCheck = (mortgage: IMortgage) => {
    if (
      (mortgage.getUrl && mortgage.lender && mortgage.amount && !mortgage.revisions.length) ||
      mortgage.revisions.includes('No MPA Required')
    ) {
      return 'Mortgage Received';
    } else {
      return 'Mortgage Missing';
    }
  };

  return (
    <>
      <Grid item lg={2} md={4} sm={6} xs={12}>
        <Link style={{ textDecoration: 'none' }} to={`/${project._id}/dashboard/${deal?.unit._id}`}>
          <Paper
            elevation={12}
            sx={{
              p: 2,
              width: '100%',
              position: 'relative',
              transition: 'transform 0.5s ease-in-out',
              '&:hover': {
                transform: 'scale(1.03)',
              },
              backgroundColor: statusColour(deal),
              cursor: 'pointer',
            }}
          >
            <strong>Suite {deal.unit.suite}</strong>
            <div style={{ fontSize: '12px' }}>{deal.realtor.map((realtor: IDealRealtor) => realtor.email).join(', ')}</div>
            {!deal.cancelled.dateCancelled && !deal.rescission.dateRescinded ? 
              <div>
                Status: {deal.unit.status} {statusDate(deal)}
              </div>
            : null}
            <div>
              Unit Price{project.netHst ? ' (Net HST)' : ''}:{' '}
              {numToCurrency.format(project.netHst ? netHST(deal.basePrice, project) : deal.basePrice)}
            </div>
            {cancelled ? (
              <Box>
                {deal.cancelled.dateCancelled ? (
                  <div>
                    <span style={{ color: 'red' }}>This deal has been cancelled</span>
                  </div>
                ) : null}
                {deal.rescission.dateRescinded ? (
                  <div>
                    <span style={{ color: 'red' }}>This deal has been rescinded on {convertDate(deal.rescission.dateRescinded)}</span>
                  </div>
                ) : null}
              </Box>
            ) : (
              <Box>
                {location.state.type === 'realtor' ? (
                  <div>Commission: {commissionCheck(deal._id!, deal.documents, deal.realtor)}</div>
                ) : null}
                <div>
                  <span style={{ color: mortgageCheck(deal.mortgage) === 'Mortgage Received' ? 'green' : '#af0000' }}>
                    {mortgageCheck(deal.mortgage)}
                  </span>
                </div>
                <div>
                  <span style={{ color: depositCheck(deal.deposit) === 'Deposits Received' ? 'green' : '#af0000' }}>
                    {depositCheck(deal.deposit)}
                  </span>
                </div>
              </Box>
            )}
          </Paper>
        </Link>
      </Grid>
    </>
  );
};

interface ChildProps {
  deal: IDeal;
  coopRates: ICoopDeal[];
  location: any;
  cancelled: boolean;
}

export default DealCard;
